import axiosInstance from "../config/axios";
import API_ENDPOINTS from "../config/endpoints";

const buildImageUrl = (imagePath) => {
  if (!imagePath) return "";
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }
  const imageBase =
    import.meta.env.VITE_API_IMAGE_PATH || import.meta.env.VITE_API_BASE_URL || "";
  if (imageBase && !imagePath.startsWith("/")) {
    return `${imageBase}/${imagePath}`;
  }
  if (imageBase) {
    return `${imageBase}${imagePath}`;
  }
  return imagePath;
};

const normalizeTopDonor = (donor) => ({
  id: donor?.id ?? `${donor?.userId ?? ""}-${donor?.createdAt ?? ""}`,
  name: donor?.name || donor?.User?.name || "",
  amount: donor?.amount ?? "",
  currency: donor?.currency ?? "",
  donationDate: donor?.createdAt ?? null,
  avatar: buildImageUrl(donor?.User?.profile_picture || ""),
});

const normalizeRecentDonor = (donor, index) => ({
  id: donor?.id ?? donor?.transactionId ?? `${donor?.userId ?? ""}-${donor?.donationDate ?? ""}-${donor?.amount ?? ""}-${index}`,
  name: donor?.name || "",
  amount: donor?.amount ?? "",
  currency: donor?.currency ?? "",
  donationDate: donor?.donationDate ?? null,
  avatar: buildImageUrl(donor?.profilePicture || ""),
});

export const getTopDonorsByCampaign = async (campaignId) => {
  if (!campaignId) {
    return [];
  }

  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_TOP_DONORS(campaignId));
    const donors = Array.isArray(response?.data) ? response.data : response?.data?.data;
    if (!Array.isArray(donors)) {
      return [];
    }
    return donors.map(normalizeTopDonor);
  } catch (error) {
    throw error;
  }
};

export const getRecentDonorsByCampaign = async (campaignId, limit = 8) => {
  if (!campaignId) {
    return [];
  }

  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_RECENT_DONORS, {
      params: { limit, campaignId },
    });

    const dataSection =
      response?.data && (response.data.domestic || response.data.overseas)
        ? response.data
        : response?.data?.data;

    const domestic = Array.isArray(dataSection?.domestic) ? dataSection.domestic : [];
    const overseas = Array.isArray(dataSection?.overseas) ? dataSection.overseas : [];
    const combined = [...domestic, ...overseas]
      .map((d, i) => normalizeRecentDonor(d, i))
      .sort(
        (a, b) =>
          new Date(b.donationDate || 0).getTime() -
          new Date(a.donationDate || 0).getTime()
      );
    // Deduplicate so the same donation is not shown twice (e.g. if API returns in both domestic and overseas)
    const seenIds = new Set();
    const seenKey = new Set(); // name+amount+date fallback for when id is not unique
    return combined.filter((d) => {
      if (seenIds.has(d.id)) return false;
      const key = `${d.name}|${d.amount}|${d.donationDate ?? ""}`;
      if (seenKey.has(key)) return false;
      seenIds.add(d.id);
      seenKey.add(key);
      return true;
    });
  } catch (error) {
    throw error;
  }
};

export const getRecentTransactions = async () => {
  try {
    // Axios interceptor already returns response.data, so response is the API response body
    const response = await axiosInstance.get(API_ENDPOINTS.GET_RECENT_TRANSACTIONS);
    return response;
  } catch (error) {
    throw error;
  }
};

